/**
 * ORCA360 Part A - Session Identification Component
 * Displays session metadata in the ORCA360 CBTA format
 * Mobile: collapsible accordion (collapsed by default)
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useDevice } from '../../contexts/DeviceContext';

export interface PartAData {
  traineeName: string;
  traineeId: string;
  instructorName: string;
  instructorId: string;
  aircraftType: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  timeAsPF: string;
  timeAsPM: string;
  location: string;
  simulatorId: string;
  sessionName: string;
  overallResult: string;
  function: string;
}

interface Props {
  data: PartAData;
}

export function ORCA360PartA({ data }: Props) {
  const { isMobile } = useDevice();
  const [expanded, setExpanded] = useState(false);

  const formatValue = (value: string | undefined | null): string => {
    if (!value || value === 'Not stated in transcript') {
      return '—';
    }
    return value;
  };

  const getResultColor = (result: string): string => {
    switch (result?.toLowerCase()) {
      case 'pass':
        return 'text-green-700 bg-green-100';
      case 'fail':
        return 'text-red-700 bg-red-100';
      case 'incomplete':
        return 'text-yellow-700 bg-yellow-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const sessionRows = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-4">
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Trainee</span>
        <span className="text-sm text-navy">{formatValue(data.traineeName)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Trainee ID</span>
        <span className="text-sm text-navy">{formatValue(data.traineeId)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Instructor</span>
        <span className="text-sm text-navy">{formatValue(data.instructorName)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Instructor ID</span>
        <span className="text-sm text-navy">{formatValue(data.instructorId)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Aircraft Type/Variant</span>
        <span className="text-sm text-navy">{formatValue(data.aircraftType)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Session Date</span>
        <span className="text-sm text-navy">{formatValue(data.sessionDate)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Start (hh:mm) LT</span>
        <span className="text-sm text-navy">{formatValue(data.startTime)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">End (hh:mm) LT</span>
        <span className="text-sm text-navy">{formatValue(data.endTime)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Time as PF (hh:mm)</span>
        <span className="text-sm text-navy font-medium">{formatValue(data.timeAsPF)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Time as PM (hh:mm)</span>
        <span className="text-sm text-navy font-medium">{formatValue(data.timeAsPM)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Location</span>
        <span className="text-sm text-navy">{formatValue(data.location)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Simulator ID</span>
        <span className="text-sm text-navy">{formatValue(data.simulatorId)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Session</span>
        <span className="text-sm text-navy">{formatValue(data.sessionName)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Overall Result</span>
        <span className={`text-sm px-2 py-0.5 rounded font-medium ${getResultColor(data.overallResult)}`}>
          {formatValue(data.overallResult)}
        </span>
      </div>
      <div className="flex justify-between md:col-span-2 border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600">Function</span>
        <span className="text-sm text-navy">{formatValue(data.function)}</span>
      </div>
    </div>
  );

  /* ── Mobile: collapsible accordion (collapsed by default) ── */
  if (isMobile) {
    return (
      <div className="border-y border-slate-200 bg-white overflow-hidden">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-2 px-2 py-2 text-left touch-target"
        >
          {expanded ? (
            <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
          )}
          <span className="text-sm font-bold text-navy">Part A — Session Identification</span>
          {!expanded && (
            <span className="ml-auto text-[10px] text-slate-400 truncate max-w-[40%]">
              {formatValue(data.traineeName)} • {formatValue(data.sessionDate)}
            </span>
          )}
        </button>
        {expanded && (
          <div className="px-2 pb-2 border-t border-slate-100 pt-2">
            {sessionRows}
          </div>
        )}
      </div>
    );
  }

  /* ── Desktop: always open card ── */
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2">
        Part A — Session Identification
      </h2>
      {sessionRows}
    </div>
  );
}

export default ORCA360PartA;

